/**
 * NotificationCenter 组件
 * 通知中心组件，支持通用通知类型
 */

import { useState, useEffect, memo } from 'react'
import { Bell } from 'lucide-react'
import { NotificationItem } from './NotificationItem'
import type { Notification } from './types'

interface NotificationCenterProps {
  notifications: Notification[]
  onNotificationClick?: (notification: Notification) => void
  onRetry?: (notificationId: string) => void
  onClearAll?: () => void
  onMarkAllRead?: () => void
  maxNotifications?: number
  enableLocalStorage?: boolean
  storageKey?: string
}

// 本地存储管理（可选）
const NOTIFICATIONS_STORAGE_KEY = 'axiom_notifications'
const MAX_NOTIFICATIONS = 50

export const loadNotificationsFromStorage = (storageKey: string = NOTIFICATIONS_STORAGE_KEY): Notification[] => {
  try {
    const stored = localStorage.getItem(storageKey)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export const saveNotificationsToStorage = (
  notifications: Notification[],
  storageKey: string = NOTIFICATIONS_STORAGE_KEY,
  maxNotifications: number = MAX_NOTIFICATIONS
) => {
  try {
    const toSave = notifications.slice(0, maxNotifications)
    localStorage.setItem(storageKey, JSON.stringify(toSave))
  } catch {
    // ignore
  }
}

export const addNotificationToStorage = (
  notification: Omit<Notification, 'id' | 'timestamp' | 'read'>,
  storageKey: string = NOTIFICATIONS_STORAGE_KEY
): Notification => {
  const newNotification: Notification = {
    ...notification,
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    read: false,
  }
  
  const notifications = loadNotificationsFromStorage(storageKey)
  notifications.unshift(newNotification)
  saveNotificationsToStorage(notifications, storageKey)
  
  return newNotification
}

export const NotificationCenter = memo(({
  notifications,
  onNotificationClick,
  onRetry,
  onClearAll,
  onMarkAllRead,
  maxNotifications = MAX_NOTIFICATIONS,
  enableLocalStorage: _enableLocalStorage = false,
  storageKey: _storageKey = NOTIFICATIONS_STORAGE_KEY,
}: NotificationCenterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  // 未读数量
  const unreadCount = notifications.filter(n => !n.read).length

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.notification-center')) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  // 重新尝试处理
  const handleRetry = async (notificationId: string) => {
    if (retryingId || !onRetry) return

    setRetryingId(notificationId)
    try {
      await onRetry(notificationId)
    } catch (err) {
      console.error('Retry failed:', err)
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <div className="relative notification-center">
      {/* 通知铃铛按钮 */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
          if (!isOpen) {
            onMarkAllRead?.()
          }
        }}
        className="relative p-2 rounded-xl transition-colors hover:opacity-80 bg-slate-800"
        title="通知中心"
      >
        <Bell className="w-5 h-5 text-slate-400" />
        {/* 未读红点 */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full text-white bg-red-500">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 通知面板 */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-hidden rounded-xl shadow-2xl z-50 bg-slate-900 border border-slate-800">
          {/* 标题栏 */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-800">
            <div className="flex items-center gap-2">
              <span className="text-lg">📬</span>
              <span className="font-medium text-slate-100">
                通知中心
              </span>
            </div>
            {notifications.length > 0 && onClearAll && (
              <button
                onClick={onClearAll}
                className="text-xs px-2 py-1 rounded-lg transition-colors hover:opacity-80 text-slate-400 bg-slate-800"
              >
                清空全部
              </button>
            )}
          </div>

          {/* 通知列表 */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">暂无通知</p>
              </div>
            ) : (
              notifications.slice(0, maxNotifications).map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRetry={handleRetry}
                  retryingId={retryingId}
                  onClick={onNotificationClick}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
})

NotificationCenter.displayName = 'NotificationCenter'

